/**
 * ログイン (パスワードレス)。
 *
 * スプリは会員登録なしでも全機能を使えるため、この画面は
 * 「複数端末で履歴を共有したい人向けのオプション」という位置づけ。
 *
 * そのオプションのために覚えるものを増やしたくないので、パスワードは使わない。
 * メールアドレスに届く確認コードだけで認証する。
 * 「会員登録」と「ログイン」の区別も無く、初回のコード入力がそのまま登録になる。
 */
import { useRequestLoginCode, useSpli, useVerifyLoginCode } from "@spli/app-logic";
import { LOGIN_CODE_LENGTH } from "@spli/core";
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { ErrorView } from "../components/StatusViews.js";
import { Icon } from "../components/Icon.js";

export function LoginPage() {
  const { user } = useSpli();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  /** コードを送ったあとは入力画面に切り替える */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [expiresInMinutes, setExpiresInMinutes] = useState(10);
  /** 開発環境でだけ返るコード。メールを送れない環境で確認するため */
  const [devCode, setDevCode] = useState<string | null>(null);

  const requestCode = useRequestLoginCode();
  const verifyCode = useVerifyLoginCode();

  if (user) return <Navigate to="/history" replace />;

  const handleRequest = (event: FormEvent): void => {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    requestCode.mutate(
      { email: address },
      {
        onSuccess: (result) => {
          setSentTo(address);
          setExpiresInMinutes(result.expiresInMinutes);
          setDevCode(result.devCode);
          setCode("");
        },
      },
    );
  };

  const handleVerify = (event: FormEvent): void => {
    event.preventDefault();
    if (!sentTo) return;
    verifyCode.mutate({ email: sentTo, code: code.trim() });
  };

  const backToEmail = (): void => {
    setSentTo(null);
    setCode("");
    setDevCode(null);
    requestCode.reset();
    verifyCode.reset();
  };

  return (
    <section className="card">
      <h1 className="card__title">ログイン</h1>
      <p className="card__hint">
        アカウントは任意です。作成すると、参加したグループの履歴をどの端末からでも
        見られるようになり、受け取り完了を本人として記録できます。
        <br />
        パスワードはありません。メールアドレスに届く確認コードだけでログインできます。
      </p>

      {sentTo === null ? (
        <form onSubmit={handleRequest} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="auth-email">
              メールアドレス
            </label>
            <input
              id="auth-email"
              className="input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          {requestCode.isError && <ErrorView error={requestCode.error} />}

          <button type="submit" className="button button--block" disabled={requestCode.isPending}>
            {requestCode.isPending ? "送信中…" : "確認コードを送る"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerify} noValidate>
          <p className="notice" style={{ marginBottom: 16 }} role="status">
            <Icon name="mail-sent" size={18} /> <strong>{sentTo}</strong> に確認コードを送りました。
            <br />
            {expiresInMinutes} 分以内に入力してください。
          </p>

          {/*
            メールを送れない開発環境では、コードを画面に出して動作確認できるようにする。
            本番のサーバーはこの値を返さない。
          */}
          {devCode && (
            <p className="notice notice--warning" style={{ marginBottom: 16 }}>
              開発環境のため、確認コードをここに表示しています: <strong>{devCode}</strong>
            </p>
          )}

          <div className="field">
            <label className="field__label" htmlFor="auth-code">
              確認コード ({LOGIN_CODE_LENGTH} 桁)
            </label>
            <input
              id="auth-code"
              className="input input--code"
              // 数字だけなので、スマホでテンキーを出す
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={LOGIN_CODE_LENGTH}
              placeholder={"0".repeat(LOGIN_CODE_LENGTH)}
              value={code}
              // 貼り付けたときに空白やハイフンが混じっても通るようにする
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            />
          </div>

          {verifyCode.isError && <ErrorView error={verifyCode.error} />}
          {requestCode.isError && <ErrorView error={requestCode.error} />}

          <button
            type="submit"
            className="button button--block"
            disabled={verifyCode.isPending || code.length !== LOGIN_CODE_LENGTH}
          >
            {verifyCode.isPending ? "確認中…" : "ログイン"}
          </button>

          <div className="field__actions" style={{ marginTop: 12 }}>
            <button type="button" className="link-button" onClick={backToEmail}>
              メールアドレスを変える
            </button>
            <button
              type="button"
              className="link-button"
              disabled={requestCode.isPending}
              onClick={() =>
                requestCode.mutate(
                  { email: sentTo },
                  {
                    onSuccess: (result) => {
                      setDevCode(result.devCode);
                      setCode("");
                    },
                  },
                )
              }
            >
              {requestCode.isPending ? "送信中…" : "コードを再送する"}
            </button>
          </div>
        </form>
      )}

      <p className="muted" style={{ marginTop: 16 }}>
        <Link to="/">アカウントなしで使う</Link>
      </p>
    </section>
  );
}
